import { createContext, memo, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { ConsoleResponse } from '@/api/types';

// ─── Store ────────────────────────────────────────────────────────────

interface ImmediateResultStore {
  current: ConsoleResponse | null;
  set: (block: ConsoleResponse | null) => void;
}

const Ctx = createContext<ImmediateResultStore | null>(null);

export const ImmediateResultProvider = ({ children }: { children: ReactNode }) => {
  const [current, set] = useState<ConsoleResponse | null>(null);
  const value = useMemo(() => ({ current, set }), [current]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useImmediateResult = (): ImmediateResultStore => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useImmediateResult must be used within ImmediateResultProvider');
  return ctx;
};

// ─── Component ────────────────────────────────────────────────────────

const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${label}`, { duration: 1500 });
  } catch {
    toast.error('Copy failed');
  }
};

const CopyButton = memo(function CopyButton({
  text, label, className,
}: { text: string; label: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); copyToClipboard(text, label); }}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-zinc-500',
        'hover:bg-white/5 hover:text-zinc-200 transition-colors',
        className,
      )}
      title={`Copy ${label}`}
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
});

interface ImmediateResultBlockProps {
  block: ConsoleResponse;
  className?: string;
}

export const ImmediateResultBlock = memo(function ImmediateResultBlock({
  block, className,
}: ImmediateResultBlockProps) {
  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-2xl border border-white/[0.06]',
        'bg-zinc-900/60 backdrop-blur-xl',
        className,
      )}
    >
      <div className="max-h-[75vh] overflow-auto">
        {block.sections.map((section, i) => (
          <div key={i} className="group relative">
            {i > 0 && <div className="mx-4 border-t border-white/[0.06]" />}
            <pre
              className={cn(
                'm-0 px-4 py-3 font-mono text-[12.5px] leading-[1.55] text-zinc-200',
                'whitespace-pre-wrap break-words',
              )}
            >
              {section || ' '}
            </pre>
            <CopyButton
              text={section}
              label={`section ${i + 1}`}
              className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        ))}
      </div>
    </div>
  );
});


export const ImmediateResultSlot = ({ className }: { className?: string }) => {
  const { current, set } = useImmediateResult();
  // Clear on unmount so navigating away from the page (e.g. landing →
  // chat or back) doesn't leave a stale block hanging around when the
  // user returns.
  useEffect(() => {
    return () => set(null);
    // `set` is stable from the provider; intentionally no other deps so
    // we only clear on actual unmount, not on every block update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!current) return null;
  return (
    <div className={cn('w-full shrink-0 px-4 pt-3 pb-4', className)}>
      <div className="mx-auto max-w-2xl">
        <ImmediateResultBlock block={current} />
      </div>
    </div>
  );
};
